import { Injectable } from '@angular/core';
import { StateStore } from '@shared/store/state-store';
import { PlatformUser } from '@/app/platform/platform-core/user/models/platform-user';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';
import { ValidAuthentication } from '@/app/platform/platform-core/user/services/types';
import { jwtLifetimeMS } from '@/app/platform/platform-core/user/services/jwt-token-lifetime';
import { environment } from '@/environments/environment';

export interface UserState {
  authenticatedUser: PlatformUser | undefined;
  authenticatedUserToken: string;
  /**
   * Toesteltijd (ms) vanaf wanneer het token vóór gebruik eerst vernieuwd moet worden;
   * undefined = geen levensduur bekend. Zie setAuthenticatedUser.
   */
  authenticatedUserTokenRenewAt: number | undefined;
  authenticatedUserApps: UserEgiApp[];
  presentUserNames: string[];
  userHasJustChanged: boolean;
}

export function createInitialState(): UserState {
  return {
    authenticatedUser: undefined,
    authenticatedUserApps: [],
    authenticatedUserToken: '',
    authenticatedUserTokenRenewAt: undefined,
    presentUserNames: [],
    userHasJustChanged: false
  };
}

/**
 * Een token dat van een andere tab komt (hergebruikte auth) kan tot één refresh-interval
 * ouder zijn dan zijn ontvangst hier; de extra minuut dekt netwerk en afronding.
 */
function tokenRenewalMarginMS(): number {
  return environment.auth.refreshTokenIntervalMS + 60 * 1000;
}

/**
 * Gemeten vanaf ontvangst op dit toestel, met de levensduur die de server meegaf: zo telt
 * een scheve toestelklok niet mee. Normaal ververst de refresh-tick het token elke minuut en
 * komt dit tijdstip nooit in zicht; enkel een bevroren tab (iOS Safari op de achtergrond,
 * laptop in slaap) loopt er voorbij.
 */
function tokenRenewAt(jwtToken: string): number | undefined {
  const lifetimeMS = jwtLifetimeMS(jwtToken);
  if (lifetimeMS === undefined) return undefined;
  return Date.now() + Math.max(lifetimeMS - tokenRenewalMarginMS(), lifetimeMS / 2);
}

@Injectable({ providedIn: 'root' })
export class UserStore extends StateStore<UserState> {
  constructor() {
    super(createInitialState);
  }

  setAuthenticatedUser(v: ValidAuthentication): void {
    v.apps.forEach(a => a.rootNavigationItem.sortDescendants());
    const { authenticatedUserToken, authenticatedUserTokenRenewAt } = this.getValue();
    this.update({
      authenticatedUserApps: v.apps,
      authenticatedUser: v.user,
      authenticatedUserToken: v.jwtToken,
      // Hetzelfde token opnieuw (hergebruikte auth) houdt zijn oorspronkelijke ontvangst.
      authenticatedUserTokenRenewAt:
        v.jwtToken === authenticatedUserToken ? authenticatedUserTokenRenewAt : tokenRenewAt(v.jwtToken),
      userHasJustChanged: true
    });
  }

  clearAuthenticatedUser(): void {
    this.update({
      authenticatedUser: undefined,
      authenticatedUserToken: '',
      authenticatedUserTokenRenewAt: undefined,
      authenticatedUserApps: []
    });
  }
}
